import { DocumentNode, gql, useMutation } from '@apollo/client';
import { Box, Image, ImageProps } from '@chakra-ui/react';
import * as React from 'react';

import { EPrepImagesSource } from '@/graphql-types/globalTypes';
import { useGroupPermissions } from '@/hooks/useGroupPermissions';
import { useImageUpload } from '@/hooks/useImageUpload';
import { ImageFallbackMavericks } from '@/imports/ui/chakra/components/ImageFallbackMavericks';
import { BannerImageMenu } from '@/imports/ui/components/BannerImageMenu';

import { GroupHeaderBanner_GroupFragment } from './graphql-types/GroupHeaderBanner_GroupFragment';
import {
  GroupHeaderBanner_UpdateGroup,
  GroupHeaderBanner_UpdateGroupVariables,
} from './graphql-types/GroupHeaderBanner_UpdateGroup';

const GROUP_FRAGMENT = gql`
  fragment GroupHeaderBanner_GroupFragment on Group {
    _id
    bannerImage {
      _id
      imageProxy(input: { ar: "8:3", w: 1600, fit: "fill", fill: "blur" })
    }
  }
`;

const UPDATE_GROUP = gql`
  mutation GroupHeaderBanner_UpdateGroup($input: UpdateGroupInput!) {
    updateGroup(input: $input) {
      data {
        ...GroupHeaderBanner_GroupFragment
      }
    }
  }
  ${GROUP_FRAGMENT}
`;

export const GroupHeaderBanner: React.FC<
  {
    group: GroupHeaderBanner_GroupFragment;
  } & ImageProps
> & {
  groupFragment: DocumentNode;
} = ({ group, ...imageProps }) => {
  const userGroupPermissions = useGroupPermissions(group._id);
  const canEdit = userGroupPermissions.canEditBannerImage;

  const [updateFunc] = useMutation<
    GroupHeaderBanner_UpdateGroup,
    GroupHeaderBanner_UpdateGroupVariables
  >(UPDATE_GROUP);

  const { uploadImages } = useImageUpload(EPrepImagesSource.DEFAULT);

  const [isLoading, setIsLoading] = React.useState(false);

  const update = async (file: any): Promise<any> => {
    setIsLoading(true);
    const resource = await uploadImages([file]);
    if (resource) {
      await updateFunc({
        variables: {
          input: {
            id: group._id,
            data: { groupBannerImageId: resource[0] },
          },
        },
      });
    }
    setIsLoading(false);
  };

  return (
    <Box position="relative">
      <Image
        borderTopRadius="xl"
        src={group.bannerImage?.imageProxy ?? undefined}
        alt=""
        fallback={<ImageFallbackMavericks borderTopRadius="xl" title="" />}
        {...imageProps}
      />

      {canEdit && (
        <BannerImageMenu
          hasBannerImage={!!group.bannerImage?.imageProxy}
          allowRemove={false}
          updateBannerImage={update}
          isLoading={isLoading}
        />
      )}
    </Box>
  );
};
GroupHeaderBanner.groupFragment = GROUP_FRAGMENT;
